export type EmploymentAdminPhase =
  | 'not_started'
  | 'retryable_review'
  | 'forwarded_under_review'
  | 'verified'
  | 'rejected';

const EMPLOYMENT_ACTIONS = new Set(['EMPLOYMENT_RUN', 'EMPLOYMENT_REUSE_PRIOR']);

export function hasMilestone(milestones: string[] | undefined, code: string): boolean {
  return (milestones ?? []).includes(code);
}

export function readForwardFlags(lastActionResult: Record<string, unknown> | null | undefined): {
  forwardUnderReview: boolean;
  attemptExhausted: boolean;
} {
  return {
    forwardUnderReview: lastActionResult?.forwardUnderReview === true,
    attemptExhausted: lastActionResult?.attemptExhausted === true,
  };
}

export function isEmploymentForwardedUnderReview(input: {
  milestones?: string[];
  lastActionResult?: Record<string, unknown> | null;
}): boolean {
  if (hasMilestone(input.milestones, 'EMPLOYMENT_PENDING_REVIEW')) return true;
  const flags = readForwardFlags(input.lastActionResult);
  return flags.forwardUnderReview || flags.attemptExhausted;
}

export function isAttemptCapForward(input: {
  lastActionResult?: Record<string, unknown> | null;
}): boolean {
  return readForwardFlags(input.lastActionResult).attemptExhausted;
}

export function isGigPlatformForward(input: {
  lastActionResult?: Record<string, unknown> | null;
}): boolean {
  const flags = readForwardFlags(input.lastActionResult);
  return flags.forwardUnderReview && !flags.attemptExhausted;
}

export function resolveEmploymentAdminPhase(input: {
  milestones?: string[];
  latestFinalStatus?: string | null;
  applicationStatus?: string | null;
  lastAction?: string | null;
  lastActionResult?: Record<string, unknown> | null;
}): EmploymentAdminPhase {
  const finalStatus = (input.latestFinalStatus ?? '').trim().toUpperCase();
  const appStatus = (input.applicationStatus ?? '').trim().toUpperCase();

  if (appStatus === 'DECLINED' || finalStatus === 'REJECTED') {
    return 'rejected';
  }

  if (hasMilestone(input.milestones, 'EMPLOYMENT_VERIFIED') || finalStatus === 'VERIFIED') {
    return 'verified';
  }

  if (isEmploymentForwardedUnderReview(input)) {
    return 'forwarded_under_review';
  }

  if (
    finalStatus === 'MANUAL_REVIEW' ||
    (input.lastAction && EMPLOYMENT_ACTIONS.has(input.lastAction))
  ) {
    return 'retryable_review';
  }

  return 'not_started';
}

export function isEmploymentLastAction(action: string | null | undefined): boolean {
  if (!action) return false;
  return EMPLOYMENT_ACTIONS.has(action);
}
